import { err, ok, type Result } from "neverthrow";
import { z } from "zod";

/** 只由店面入口提供；请求始终发往买家当前店面的同源路径。 */
export interface StorefrontCart {
  rootUrl: string;
  shopDomain: string;
  /** 店面语言：加购面板按它格式化金额。 */
  locale?: string;
}

/** 商品的一个选项（颜色、尺码……），取值按店面给的顺序。 */
export interface CartOption {
  name: string;
  values: string[];
}

export interface CartVariant {
  id: number;
  /** 变体在各选项上的取值，和 CartProductDetail.options 一一对应；只有默认变体时为空。 */
  values: string[];
  /** Shopify 的变体标题，如 `White / M`；默认变体为空。 */
  title: string;
  /** 店面当前币种的最小单位（分）；店面没给就不显示单价。 */
  price?: number;
  available: boolean;
  imageUrl?: string;
}

/**
 * 店面公开商品接口（`products/<handle>.js`）能给的全部购买信息。
 * 这个接口不给库存数、每单限购和色块，所以数量上限只能等 Shopify 写入后才知道。
 */
export interface CartProductDetail {
  options: CartOption[];
  variants: CartVariant[];
  /** 只能按订阅方案购买：方案要在商品页选，挂件不替买家选。 */
  sellingPlanOnly: boolean;
}

/** 购物车里的一行。properties 或订阅方案不同的同一变体是分开的几行。 */
export interface CartLine {
  key: string;
  variantId: number;
  quantity: number;
  /** 没有 properties、没有订阅方案：挂件自己加购写进的就是这种行。 */
  plain: boolean;
}

export interface CartLines {
  currency: string;
  lines: CartLine[];
}

/**
 * 写购物车的失败分三种：Shopify 明确没写入（failed），拒绝并给了理由（rejected，
 * 通常是库存或限购），以及请求可能已写入却没拿到结果（unknown）。
 */
export type CartWriteError =
  | { status: "failed" }
  | { status: "rejected"; message?: string }
  | { status: "unknown" };

/** 发送时购物车快照的单条商品行：白名单字段，与 send-message 契约对齐。 */
export interface CartContextItem {
  product_id: string;
  variant_id: string | null;
  product_title: string | null;
  variant_title: string | null;
  sku: string | null;
  quantity: number;
  /** final_price 由分转十进制字符串；只作展示语境，不作交易依据。 */
  price: string | null;
  product_url: string | null;
}

/** 发送时购物车快照；匿名、只读，不含 token/key/note/attributes/properties 等凭证或私密字段。 */
export interface CartContext {
  currency: string;
  /** 全车件数（quantity 求和），在 items 截断之前统计。 */
  item_count: number;
  /** total_price 由分转十进制字符串；只作展示语境，不作交易依据。 */
  total_price: string;
  /** 有商品行因条目或体积预算被裁掉时为 true。 */
  truncated: boolean;
  items: CartContextItem[];
}

const variantSchema = z.object({
  id: z.number().int().positive(),
  available: z.boolean(),
  title: z.string().nullish(),
  price: z.number().int().nonnegative().optional().catch(undefined),
  options: z.array(z.string()).nullish(),
  option1: z.string().nullish(),
  option2: z.string().nullish(),
  option3: z.string().nullish(),
  featured_image: z.object({ src: z.string() }).nullish().catch(undefined),
});
const productSchema = z.object({
  // 老主题和新接口两种写法：选项名字符串，或带取值的对象。
  options: z
    .array(
      z.union([
        z.string(),
        z.object({ name: z.string(), values: z.array(z.string()).optional() }),
      ]),
    )
    .optional(),
  variants: z.array(variantSchema).min(1),
  requires_selling_plan: z.boolean().optional(),
});
const addedSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.number().int().positive(),
        key: z.string().min(1).optional(),
        quantity: z.number().int().positive(),
      }),
    )
    .length(1),
});
const lineSchema = z.object({
  key: z.string().min(1),
  variant_id: z.number().int().positive(),
  quantity: z.number().int().nonnegative(),
  properties: z.record(z.string(), z.unknown()).nullish(),
  selling_plan_allocation: z.unknown().optional(),
});
const linesSchema = z.object({
  currency: z.string().min(1),
  items: z.array(z.unknown()),
});
const rejectionSchema = z.object({ description: z.string().min(1) });

/** `products/<handle>.js` 最多给 250 个变体，到了这个数就可能缺了后面的。 */
const PRODUCT_JSON_VARIANT_LIMIT = 250;
/** Shopify 没有变体可选时给的那一个默认变体，取值是协议里的固定值 `Default Title`。 */
const DEFAULT_VARIANT_VALUE = /^default title$/i;
const CART_WRITE_TIMEOUT_MS = 8_000;
/** 写入前后核对数量的读取：读不到就只能说「没法确认」，所以比快照读取多等一会儿。 */
const CART_CONFIRM_READ_MS = 5_000;

/** 购物车快照的独立读取预算：不能拖住买家消息的发送。 */
const CART_READ_TIMEOUT_MS = 3_000;
const CART_ITEM_LIMIT = 20;
const CART_PAYLOAD_BYTES = 4_096;

const snapshotItemSchema = z.object({
  product_id: z.number().int().positive(),
  variant_id: z.number().int().positive().nullish(),
  product_title: z.string().nullish(),
  variant_title: z.string().nullish(),
  sku: z.string().nullish(),
  // 部分店面只回组合标题 "Product - Variant"，当作 product_title 的兜底。
  title: z.string().nullish(),
  quantity: z.number().int().positive(),
  final_price: z.number().int().nonnegative().nullish(),
  price: z.number().int().nonnegative().nullish(),
  url: z.string().nullish(),
});
const snapshotSchema = z.object({
  currency: z.string().min(1),
  item_count: z.number().int().nonnegative(),
  total_price: z.number().int().nonnegative(),
  items: z.array(snapshotItemSchema),
});

const decimalPrice = (cents: number | null | undefined): string | null =>
  cents === null || cents === undefined ? null : (cents / 100).toFixed(2);

const clip = (text: string | null | undefined, max: number): string | null =>
  text === null || text === undefined
    ? null
    : text.length > max
      ? text.slice(0, max)
      : text;

const idString = (id: number | null | undefined): string | null =>
  id === null || id === undefined ? null : String(id);

const byteSize = (value: unknown): number =>
  new TextEncoder().encode(JSON.stringify(value)).length;

function sameOriginUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw, location.origin);
    return url.origin === location.origin ? url.href : null;
  } catch {
    return null;
  }
}

/** 变体图常是 `//cdn.shopify.com/...` 这种省略协议的地址；只收 http(s)。 */
function imageUrl(src: string | undefined): string | undefined {
  if (!src) return undefined;
  try {
    const url = new URL(src, location.href);
    return ["http:", "https:"].includes(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
}

/**
 * 把 Shopify 的商品数据整理成选项和变体；对不上号（取值个数和选项数不一致）就当读取失败。
 * 变体可能被截断的商品也当读取失败：缺了的变体选不到、还会被算成售罄，交给商品页去买。
 */
function readDetail(
  product: z.infer<typeof productSchema>,
): CartProductDetail | undefined {
  if (product.variants.length >= PRODUCT_JSON_VARIANT_LIMIT) return undefined;
  const variants = product.variants.map((variant) => ({
    id: variant.id,
    values:
      variant.options ??
      [variant.option1, variant.option2, variant.option3].filter(
        (value): value is string => typeof value === "string",
      ),
    title: variant.title ?? "",
    price: variant.price,
    available: variant.available,
    imageUrl: imageUrl(variant.featured_image?.src),
  }));
  const only = variants.length === 1 ? variants[0] : undefined;
  if (only && only.values.every((value) => DEFAULT_VARIANT_VALUE.test(value)))
    return {
      options: [],
      variants: [{ ...only, values: [], title: "" }],
      sellingPlanOnly: product.requires_selling_plan === true,
    };
  const declared = product.options ?? [];
  const count = variants[0]?.values.length ?? 0;
  if (
    declared.length !== count ||
    variants.some((variant) => variant.values.length !== count)
  )
    return undefined;
  const options = declared.map((option, index) => {
    const named = typeof option === "string" ? { name: option } : option;
    // 店面声明的取值在前，变体里出现、声明里漏掉的补在后面。
    const values = [...(named.values ?? [])];
    for (const variant of variants) {
      const value = variant.values[index];
      if (value !== undefined && !values.includes(value)) values.push(value);
    }
    return { name: named.name, values };
  });
  return {
    options,
    variants,
    sellingPlanOnly: product.requires_selling_plan === true,
  };
}

export async function readCartProduct(
  url: string,
  storefront: StorefrontCart,
): Promise<Result<CartProductDetail, "failed">> {
  try {
    const productUrl = new URL(url, location.origin);
    const handle = /\/products\/([^/]+)\/?$/.exec(productUrl.pathname)?.[1];
    if (
      !handle ||
      !["http:", "https:"].includes(productUrl.protocol) ||
      (productUrl.origin !== location.origin &&
        productUrl.host !== storefront.shopDomain)
    ) {
      return err("failed");
    }
    const response = await fetch(`${storefront.rootUrl}products/${handle}.js`, {
      credentials: "same-origin",
      signal: AbortSignal.timeout(8_000),
    });
    if (!response.ok) return err("failed");
    const body: unknown = await response.json();
    const parsed = productSchema.safeParse(body);
    if (!parsed.success) return err("failed");
    const detail = readDetail(parsed.data);
    return detail ? ok(detail) : err("failed");
  } catch {
    return err("failed");
  }
}

/** 读当前购物车的商品行，用来确认写入前后的数量；读不到时调用方不能自己推算。 */
export async function readCartLines(
  storefront: StorefrontCart,
): Promise<Result<CartLines, "failed">> {
  try {
    const response = await fetch(`${storefront.rootUrl}cart.js`, {
      credentials: "same-origin",
      signal: AbortSignal.timeout(CART_CONFIRM_READ_MS),
    });
    if (!response.ok) return err("failed");
    const lines = readLines(await response.json());
    return lines ? ok(lines) : err("failed");
  } catch {
    return err("failed");
  }
}

function readLines(body: unknown): CartLines | undefined {
  const parsed = linesSchema.safeParse(body);
  if (!parsed.success) return undefined;
  // 单行认不出（第三方应用塞进来的自定义行）只跳过那一行，不连累整车。
  const lines = parsed.data.items.flatMap((item) => {
    const line = lineSchema.safeParse(item);
    if (!line.success) return [];
    const { key, variant_id, quantity, properties, selling_plan_allocation } =
      line.data;
    return [
      {
        key,
        variantId: variant_id,
        quantity,
        plain:
          Object.keys(properties ?? {}).length === 0 &&
          (selling_plan_allocation === undefined ||
            selling_plan_allocation === null),
      },
    ];
  });
  return { currency: parsed.data.currency, lines };
}

/** 422 是 Shopify 拒绝写入（库存、限购等），它的说明原样给买家看。 */
async function writeFailure(response: Response): Promise<CartWriteError> {
  if (response.status >= 500) return { status: "unknown" };
  if (response.status !== 422) return { status: "failed" };
  try {
    const body = rejectionSchema.safeParse(await response.json());
    return {
      status: "rejected",
      message: body.success ? body.data.description.slice(0, 200) : undefined,
    };
  } catch {
    return { status: "rejected" };
  }
}

/** 把一个变体按数量加进购物车。库存不够时 Shopify 会只加能加的数量，所以成功后要再读购物车确认。 */
export async function addToCart(
  id: number,
  quantity: number,
  storefront: StorefrontCart,
): Promise<Result<{ key?: string; quantity: number }, CartWriteError>> {
  try {
    const response = await fetch(`${storefront.rootUrl}cart/add.js`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id, quantity }] }),
      signal: AbortSignal.timeout(CART_WRITE_TIMEOUT_MS),
    });
    if (!response.ok) return err(await writeFailure(response));
    const body: unknown = await response.json();
    const parsed = addedSchema.safeParse(body);
    const item = parsed.success ? parsed.data.items[0] : undefined;
    return item?.id === id
      ? ok({ key: item.key, quantity: item.quantity })
      : err({ status: "unknown" });
  } catch {
    // 请求可能已写入；不能宣称失败后自动重试，否则会多加。
    return err({ status: "unknown" });
  }
}

/** 改购物车里一行的数量（`cart/change.js`），返回改完后的整车。 */
export async function changeCartLine(
  key: string,
  quantity: number,
  storefront: StorefrontCart,
): Promise<Result<CartLines, CartWriteError>> {
  try {
    const response = await fetch(`${storefront.rootUrl}cart/change.js`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: key, quantity }),
      signal: AbortSignal.timeout(CART_WRITE_TIMEOUT_MS),
    });
    if (!response.ok) return err(await writeFailure(response));
    const lines = readLines(await response.json());
    return lines ? ok(lines) : err({ status: "unknown" });
  } catch {
    return err({ status: "unknown" });
  }
}

/**
 * 发送一条买家消息前读取 Shopify 匿名购物车快照（`GET {rootUrl}cart.js`）。
 * 真实空车返回规范空车对象；失败、超时、
 * 响应不合约定都返回 err("failed")，绝不阻塞或中断消息发送。
 * 裁剪规则：先按条目上限截尾，再按序列化字节上限继续截尾并标记 truncated。
 */
export async function readCartSnapshot(
  storefront: StorefrontCart,
  timeoutMs = CART_READ_TIMEOUT_MS,
): Promise<Result<CartContext, "failed">> {
  const controller = new AbortController();
  const timeout = setTimeout(() => {
    controller.abort();
  }, timeoutMs);
  try {
    const response = await fetch(`${storefront.rootUrl}cart.js`, {
      credentials: "same-origin",
      signal: controller.signal,
    });
    if (!response.ok) return err("failed");
    const body: unknown = await response.json();
    const parsed = snapshotSchema.safeParse(body);
    if (!parsed.success) return err("failed");
    if ((parsed.data.item_count === 0) !== (parsed.data.items.length === 0))
      return err("failed");
    if (parsed.data.item_count === 0)
      return ok({
        currency: parsed.data.currency,
        item_count: 0,
        total_price: "0.00",
        truncated: false,
        items: [],
      });
    const items = parsed.data.items
      .slice(0, CART_ITEM_LIMIT)
      .map((item): CartContextItem => ({
        product_id: String(item.product_id),
        variant_id: idString(item.variant_id),
        product_title: clip(item.product_title ?? item.title, 200),
        variant_title: clip(item.variant_title, 200),
        sku: clip(item.sku, 64),
        quantity: item.quantity,
        price: decimalPrice(item.final_price ?? item.price),
        product_url: sameOriginUrl(item.url),
      }));
    let truncated = parsed.data.items.length > items.length;
    const build = (): CartContext => ({
      currency: parsed.data.currency,
      item_count: parsed.data.item_count,
      total_price: decimalPrice(parsed.data.total_price) ?? "0.00",
      truncated,
      items,
    });
    while (items.length > 0 && byteSize(build()) > CART_PAYLOAD_BYTES) {
      items.pop();
      truncated = true;
    }
    if (items.length === 0) return err("failed");
    return ok(build());
  } catch {
    return err("failed");
  } finally {
    clearTimeout(timeout);
  }
}
